import React from "react";
import { Box, Button, TextField, Typography, Alert } from "@mui/material";
import { uploadUserPhoto } from "../api/coreApi";

const uid = (u) => String(u?.user_id || u?.id || "");

export default function UserPhotoBlock({ users = [], onDone }) {
  const [userId, setUserId] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState({ type: "", text: "" });

  // ✅ Auto-select first user when list loads/changes
  React.useEffect(() => {
    if (!userId && users.length > 0) {
      setUserId(uid(users[0]));
    }
  }, [users, userId]);

  const upload = async () => {
    setMsg({ type: "", text: "" });

    if (!userId) {
      setMsg({ type: "error", text: "Select user first" });
      return;
    }
    if (!file) {
      setMsg({ type: "error", text: "Select image first" });
      return;
    }

    setBusy(true);
    try {
      await uploadUserPhoto(userId, file);
      setFile(null);
      onDone?.();
      setMsg({ type: "success", text: "Photo uploaded ✅" });
    } catch (e) {
      setMsg({
        type: "error",
        text: e?.response?.data ? JSON.stringify(e.response.data) : "Upload failed ❌",
      });
    } finally {
      setBusy(false);
    }
  };

  if (users.length === 0) {
    return <Alert severity="info">No users available. Create a user first.</Alert>;
  }

  return (
    <Box
      sx={{
        mt: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
        alignItems: "start",
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 900, color: "text.secondary", mb: 0.5 }}>
          Select User
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        >
          {users.map((u) => (
            <option key={uid(u)} value={uid(u)}>
              {u.full_name || "-"} ({u.email || "-"})
            </option>
          ))}
        </TextField>
      </Box>

      <Box>
        <Typography sx={{ fontWeight: 900, color: "text.secondary", mb: 0.5 }}>
          Choose Photo
        </Typography>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />

        <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
          <Button variant="contained" onClick={upload} disabled={busy}>
            {busy ? "Uploading..." : "Upload"}
          </Button>

          {file ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {file.name}
            </Typography>
          ) : null}
        </Box>

        {msg.text ? (
          <Alert severity={msg.type || "info"} sx={{ mt: 1 }}>
            {msg.text}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
}